import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '../components/auth/AuthLayout';
import { AuthCard } from '../components/auth/AuthCard';
import { Button } from '../components/shared/Button';
import { Mail, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';

interface FormData {
  email: string;
}

interface FormErrors {
  email?: string;
}

export const PasswordResetRequestPage: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    email: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = t('validation.emailRequired');
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t('validation.email');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const { authService } = await import('../services/auth.service');
      await authService.requestPasswordReset(formData.email);
      setIsSuccess(true);
    } catch (error) {
      // Always show success to prevent email enumeration
      setIsSuccess(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (value: string) => {
    setFormData({ email: value });
    if (errors.email) {
      setErrors({});
    }
  };

  // Success state
  if (isSuccess) {
    return (
      <AuthLayout
        variant="reset"
        title={t('auth.resetPassword.request.success.title')}
        subtitle={t('auth.resetPassword.request.success.subtitle')}
      >
        <AuthCard
          title={t('auth.resetPassword.request.success.title')}
          subtitle={t('auth.resetPassword.request.success.subtitle')}
        >
          <div className="text-center space-y-6">
            {/* Success Icon */}
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
              <CheckCircle className="h-8 w-8 text-success" />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <p className="text-gray-700">
                {t('auth.resetPassword.request.success.message')}
              </p>
              <p className="text-sm text-slate-500">
                {t('auth.resetPassword.request.success.spamNote')}
              </p>
            </div>

            {/* Back to Login */}
            <Link to="/login">
              <Button
                variant="primary"
                size="large"
                className="w-full"
                icon={<ArrowLeft className="w-5 h-5" />}
              >
                {t('auth.resetPassword.request.success.backToLogin')}
              </Button>
            </Link>

            {/* Resend Link */}
            <button
              onClick={() => setIsSuccess(false)}
              className="text-sm text-primary hover:underline"
            >
              {t('auth.resetPassword.request.success.resend')}
            </button>
          </div>
        </AuthCard>
      </AuthLayout>
    );
  }

  // Request form state
  return (
    <AuthLayout
      variant="reset"
      title={t('auth.resetPassword.request.title')}
      subtitle={t('auth.resetPassword.request.subtitle')}
    >
      <AuthCard
        label={t('auth.resetPassword.request.cardLabel')}
        labelColor="primary"
        title={t('auth.resetPassword.request.cardTitle')}
        subtitle={t('auth.resetPassword.request.cardSubtitle')}
        footer={{
          text: t('auth.resetPassword.request.rememberPassword'),
          linkText: t('auth.resetPassword.request.backToLogin'),
          href: '/login',
        }}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.resetPassword.request.email')}
            </label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange(e.target.value)}
              placeholder={t('auth.resetPassword.request.emailPlaceholder')}
              autoComplete="email"
              className={`w-full rounded-2xl border px-4 py-3 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                errors.email ? 'border-error bg-error/5' : 'border-primary/20 bg-white'
              }`}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'email-error' : undefined}
            />
            {errors.email && (
              <p id="email-error" className="text-sm text-error mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.email}
              </p>
            )}
          </div>

          {/* Tip */}
          <div className="flex items-start gap-2 rounded-2xl border border-primary/15 bg-primary/5 p-3 text-sm text-slate-700">
            <Mail className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
            <p>
              {t('auth.resetPassword.request.tip')}
            </p>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="large"
            className="w-full"
            loading={isSubmitting}
            disabled={isSubmitting}
            icon={<Mail className="w-5 h-5" />}
          >
            {isSubmitting ? `${t('auth.resetPassword.request.cta')}...` : t('auth.resetPassword.request.cta')}
          </Button>
        </form>
      </AuthCard>
    </AuthLayout>
  );
};

export default PasswordResetRequestPage;
